/**
 * EmojiPicker — a small inline popover for choosing a single emoji.
 *
 * Used by `PageHeader` (click the page icon to change it) and by
 * `CreatePageDialog` (pick the new page's icon). No dependencies on
 * emoji-mart or similar — the catalogue is a curated 8×6 grid of common
 * document-style glyphs that match the rest of the platform's
 * emoji-led iconography.
 *
 * Positioning: the popover is `position: fixed` and we compute the
 * coordinates from the trigger element's bounding rect (caller passes
 * the element via a ref). Closes on outside-click and Escape.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

const EMOJI_GRID: string[][] = [
  // Documents & lists
  ['📄', '📝', '📋', '📑', '🗂️', '📂'],
  // Books & knowledge
  ['📚', '📖', '🔖', '💡', '🧠', '✨'],
  // Goals / process
  ['🎯', '🚀', '🧪', '📐', '⚙️', '🛠️'],
  // People & meetings
  ['👋', '🗓️', '📞', '💬', '🤝', '✅'],
  // Tech & data
  ['🔍', '🔒', '📊', '📈', '🗄️', '🧩'],
  // Misc
  ['🌱', '🔥', '⭐', '❤️', '🎉', '📌'],
]

export const EMOJI_CATALOG = EMOJI_GRID.flat()

export interface EmojiPickerProps {
  /** Currently selected emoji (highlighted in the grid). */
  value: string | null
  /** Called when the user picks a new emoji. */
  onChange: (emoji: string) => void
  /** Called when the user dismisses without picking. */
  onClose: () => void
  /** The trigger element; used to anchor the popover. */
  anchorEl: HTMLElement
}

const POPOVER_WIDTH = 264
const POPOVER_HEIGHT = 220
const GUTTER = 8

export function EmojiPicker({ value, onChange, onClose, anchorEl }: EmojiPickerProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })

  useLayoutEffect(() => {
    const rect = anchorEl.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    // Prefer below the anchor; flip above if there isn't room.
    const belowTop = rect.bottom + GUTTER
    const aboveTop = rect.top - POPOVER_HEIGHT - GUTTER
    const top = belowTop + POPOVER_HEIGHT > vh ? Math.max(GUTTER, aboveTop) : belowTop
    // Prefer right-aligned to the anchor; flip left if there isn't room.
    const rightLeft = rect.right - POPOVER_WIDTH
    const leftLeft = rect.left
    const left =
      rightLeft + POPOVER_WIDTH > vw ? Math.max(GUTTER, Math.min(leftLeft, vw - POPOVER_WIDTH - GUTTER)) : rightLeft
    setPos({ top, left })
  }, [anchorEl])

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (ref.current?.contains(t)) return
      if (anchorEl.contains(t)) return
      onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose, anchorEl])

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Pick an icon"
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: POPOVER_WIDTH }}
      className="z-50 rounded-md border border-surface-200 bg-white p-2 shadow-lg dark:border-surface-700 dark:bg-surface-800"
    >
      <div className="grid grid-cols-6 gap-1">
        {EMOJI_CATALOG.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => {
              onChange(e)
              onClose()
            }}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded text-base transition-colors',
              'hover:bg-surface-100 dark:hover:bg-surface-700',
              value === e && 'bg-brand-500/10 ring-1 ring-brand-500',
            )}
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  )
}
